import type { ServedProvenance } from '../providerLabel';

// ─── Core market data types ───────────────────────────────────────────────────

export interface Candle {
  ts: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface OptionRow {
  strike:      number;
  callOI:      number;
  putOI:       number;
  gamma:       number | null;   // per-strike gamma (real from TS; inferred from others)
  callVol:     number;
  putVol:      number;
  expiration:  string | null;
  // TradeStation: real per-strike Greeks (optional — null from other providers)
  delta?:      number | null;
  theta?:      number | null;
  vega?:       number | null;
  iv?:         number | null;
}

export interface SessionMeta {
  prevHigh?: number;
  prevLow?: number;
  pmHigh?: number;
  pmLow?: number;
  iv?: number;
  gapUp?: number;
  prevClose?: number;
}

export type DataSource = 'api' | 'csv' | 'sample';

// ─── P3-D4 provider-aware confidence (p3-pac-v1) ─────────────────────────────
//
// The TypeScript mirror of the governed representation. The provider-confidence
// evaluator and frozen policy under `server/providers/` remain the single
// source of truth for the states and the closed 14-code `PAC_*` vocabulary;
// this type only lets the analysis context carry that object without widening
// or reshaping it. (The evaluator module is deliberately not named here: a
// banked boundary test pins the set of files that may reference it by name.)
//
// PROVIDER-EVIDENCE CONFIDENCE, NOT ANALYSIS CONFIDENCE (D-P3T-5, D-P3T-24).
// It is not `analysis.confidence`, not `insights.confidenceBand`, not setup,
// regime, signal, thesis or gamma confidence, and not a UI percentage. The
// shared words HIGH / MEDIUM / LOW do not make those domains interchangeable,
// and no mapping between them is authorized. `LOW` is not a trade veto and
// `HIGH` is not an endorsement.
export type ProviderConfidenceState = 'HIGH' | 'MEDIUM' | 'LOW' | 'UNKNOWN';

export interface ProviderConfidenceComponent {
  state: ProviderConfidenceState;
  required: boolean;
  reasonCode: string | null;
}

export interface ProviderConfidence {
  contractVersion: string;
  policyVersion: string;
  state: ProviderConfidenceState;
  /** Positional, de-duplicated, NEVER sorted (D-P3N-10). */
  reasonCodes: readonly string[];
  components: {
    providerPath: ProviderConfidenceComponent & {
      provider: string | null;
      requestedProvider: string | null;
      fallbackActive: boolean | null;
      failureClass: unknown;
    };
    freshness: ProviderConfidenceComponent & {
      freshnessState: string | null;
      ageMs: number | null;
    };
    disagreement: ProviderConfidenceComponent & {
      severity: string | null;
      deltaRel: number | null;
    };
  };
}

export interface DataState {
  candles: Candle[] | null;
  options: OptionRow[] | null;
  meta: SessionMeta;
  // P3-D4 IMPL-3 (D-P3T-4): additive and OPTIONAL, a sibling of `meta` and
  // never inside it — `SessionMeta` keeps its own numeric contract. Optional
  // because the banked runtime semantics include legitimate absence, which is
  // distinct from the governed `UNKNOWN` state.
  providerConfidence?: ProviderConfidence;
  // P3 visible provider trust: which provider ACTUALLY served this symbol's
  // data, taken from governed response provenance. Also a sibling of `meta`,
  // for the same `D-P3T-4` reason. Absent means unknown — never Theta.
  provenance?: ServedProvenance | null;
  source: DataSource;
  lastUpdated: number | null;
  error: string | null;
  loading: boolean;
  // ── Chart bar interval (display-only, per-symbol) ───────────────────────────
  //
  // v1 (Phase 3): only 1min/5min/15min are end-to-end functional. The wider
  // values (1h/4h/1D/1W) are present in the type so that the UI selector can
  // surface them with the "coming soon" banner — they pass type-check at the
  // boundary but the gateway will 400 the request until 1h/4h/1D bar support
  // lands. See PHASE_3_TIMEFRAME_DESIGN.md §3 for rationale.
  //
  // The narrower union on thetaDataService.getBars() input intentionally
  // keeps the smaller set as a hard guard at the API layer.
  interval: 'tick' | '1sec' | '5sec' | '15sec' | '30sec' |
            '1min' | '5min' | '15min' |
            '1h' | '4h' | '1D' | '1W';
}

export interface WatchlistEntry {
  symbol: string;
  addedAt: number;
}

// ─── Trader horizon (Phase 3) ─────────────────────────────────────────────────
//
// Trader horizon is the user-wide intent — what kind of trade the user is
// positioning for. It is conceptually distinct from the per-symbol chart bar
// interval (DataState.interval) and is persisted globally per user.
//
// v1 ships scalp + intraday fully functional. swing + position appear as
// selectable options in the UI but display a "coming soon" banner because
// the gateway currently only supports 1m/5m/15m bar intervals end-to-end.
//
// See PHASE_3_TIMEFRAME_DESIGN.md for the full rationale.

export type TraderHorizon = 'scalp' | 'intraday' | 'swing' | 'position';

export const TRADER_HORIZONS: readonly TraderHorizon[] = [
  'scalp',
  'intraday',
  'swing',
  'position',
] as const;

/**
 * Whether each horizon is end-to-end functional in this release.
 * UI uses this to decide whether to show the "coming soon" banner.
 * Backend ignores this — the route accepts any valid TraderHorizon and
 * stores it; the limitation is purely about which bar intervals are
 * supplied by the gateway today.
 */
export const HORIZON_AVAILABLE: Readonly<Record<TraderHorizon, boolean>> = {
  scalp:    true,
  intraday: true,
  swing:    false,   // pending gateway 1h/4h/1D bar support
  position: false,   // pending gateway 1h/4h/1D bar support
};

export const DEFAULT_HORIZON: TraderHorizon = 'intraday';

export type ZoneType = 'resistance' | 'support' | 'key';
export type ZoneSig = 'High' | 'Medium' | 'Low';

export interface LiquidityZone {
  price: number;
  label: string;
  type: ZoneType;
  sig: ZoneSig;
  reason: string;
  distPct: number;
  source: 'structure' | 'options' | 'meta' | 'round' | 'smc';
  mergedWith?: string;
}

export type GammaRegime = 'Long Gamma / Pinning' | 'Short Gamma / Expansion' | 'Neutral / Transition';
export type VolRisk = 'Low' | 'Medium' | 'High';
export type InferenceConfidence = 'Low' | 'Medium' | 'High';

export interface GammaResult {
  regime: GammaRegime;
  volRisk: VolRisk;
  confidence: InferenceConfidence;
  gammaFlip: number | null;
  maxPain: number | null;
  pinStrike: number | null;
  callWall: number | null;
  putWall: number | null;
  pcRatio: number | null;
  aboveFlip: boolean;
  nearFlip: boolean;
  expectedMove: number | null;
  atmStrike: number | null;
}

export type TrapSeverity = 'High' | 'Medium' | 'Low' | 'None';
export type TrapSide = 'bull' | 'bear' | 'neutral';

export interface TrapSignal {
  type: string;
  level: number | null;
  label: string | null;
  reason: string;
  severity: TrapSeverity;
  side: TrapSide;
}

export type RegimeDominant = 'meanReversion' | 'breakout' | 'trend' | 'noTrade';

export interface RegimeScores {
  meanReversion: number;
  breakout: number;
  trend: number;
  noTrade: number;
}

export interface RegimeDriver {
  key: string;
  label: string;
  weight: number;
  impact: number;
  explanation: string;
}

export interface RegimeResult {
  scores: RegimeScores;
  dominant: RegimeDominant;
  label: string;
  confidence: number;
  posture: string;
  topDrivers: RegimeDriver[];
  curVWAP: number;
  vwapDist: number;
  rvol: number;
  levels: SessionLevels;
  phase: MarketPhase;              // RANGE | EXPANSION | POST_EXPANSION
  phaseDriver: string;             // human-readable reason for phase classification
  /**
   * Phase 2 v1 (regime modifiers): transient micro-condition that fires
   * alongside dominant. Null when no modifier qualifies. Singular; see
   * domains/market/engines/regime/regimeModifier.ts for type definitions
   * and detection rules.
   *
   * Shape is inlined here to keep shared/types from depending on engine
   * internals. Consumers needing the canonical type can import
   * RegimeModifierDetail from the engine module.
   */
  modifier?: {
    type:       string;
    severity:   'high' | 'medium';
    confidence: number;
    reason:     string;
  } | null;
}

export interface SessionLevels {
  orHigh: number;
  orLow: number;
  sessionHigh: number;
  sessionLow: number;
  open: number;
}

export interface ScoringWeights {
  vwap: number;
  structure: number;
  volume: number;
  liquidity: number;
  gamma: number;
  volatility: number;
  timeOfDay: number;
}

export const DEFAULT_WEIGHTS: ScoringWeights = {
  vwap: 22, structure: 18, volume: 16, liquidity: 12, gamma: 14, volatility: 10, timeOfDay: 8
};

export type TradeDir = 'long' | 'short' | 'either';

// ─── Setup Maturity & Alignment (v5.5 false-confluence patch) ─────────────────
// Added additively — all fields optional for backward compatibility.

export type SetupMaturity  = 'developing' | 'confirmed' | 'actionable';
export type SetupAlignment = 'aligned' | 'countertrend-unconfirmed' | 'countertrend-confirmed';

// Canonical deterministic truth about the best setup — produced by buildSymbolContext,
// consumed by AI, alerts, research snapshots, and dashboard selectors.
export interface SetupTruth {
  displaySetupType:        string | null;
  actionableSetupType:     string | null;
  actionableSetupDir:      'long' | 'short' | null;
  maturity:                SetupMaturity | null;
  alignment:               SetupAlignment | null;
  countertrend:            boolean;
  stateTransitionConfirmed: boolean;
  confirmationMissing:     string[];
}

export interface TradeSetup {
  type: string;
  dir: TradeDir;
  // Human-readable strings (displayed in UI — preserved for all consumers)
  entry: string;
  confirmation: string;
  invalidation: string;
  t1: string;
  t2: string;
  rationale: string;
  regimeFit: string;
  confidence: number;
  // Structured numeric levels — populated by setupEngine, consumed by riskEngine
  // Optional: null when the level cannot be determined from available data
  entryPrice?:   number | null;
  stopPrice?:    number | null;
  target1Price?: number | null;
  target2Price?: number | null;
  // Setup Maturity + Alignment (v5.5) — optional, populated for reversal setups
  maturity?:                SetupMaturity;
  alignment?:               SetupAlignment;
  stateTransitionConfirmed?: boolean;
  countertrend?:            boolean;        // true = setup opposes current trend/structure
  confirmationMissing?:     string[];       // human-readable list of missing confirmation signals
}

export type JournalOutcome = 'win' | 'loss' | 'breakeven' | 'open' | null;

// ─── Journal Draft — decision-first structured journal (v5.5) ─────────────────
export type JournalDecision    = 'took' | 'passed' | 'watching';
export type ExecutionQuality   = 'good' | 'ok' | 'poor';
export type EmotionState       = 'calm' | 'hesitant' | 'fomo' | 'frustrated' | 'unclear';
export type JournalDraftOutcome = 'pending' | 'worked' | 'failed' | 'invalidated' | 'no_entry';

export interface JournalDraft {
  id: string;
  createdAt: number;
  symbol: string;

  // System-generated (read-only in UI)
  title: string;
  sessionPhase: string;
  price: number | null;
  vwap: number | null;
  rvol: number | null;
  regime: string | null;
  regimeConfidence: number | null;
  smcBias: string | null;

  // Canonical setup truth (from ctx.setupTruth)
  setupActionable: boolean;
  displaySetupType: string | null;
  actionableSetupType: string | null;
  actionableSetupDir: 'long' | 'short' | null;
  setupMaturity: string | null;
  setupAlignment: string | null;
  countertrend: boolean;
  stateTransitionConfirmed: boolean;
  confirmationMissing: string[];

  // Best setup plan
  entry: string | null;
  confirmation: string | null;
  invalidation: string | null;
  t1: string | null;
  t2: string | null;
  setupConfidence: number | null;
  setupRationale: string | null;

  // AI thesis layer (read-only in UI)
  primaryThesis: string | null;
  marketState: string | null;
  keyDrivers: string[];
  uncertainty: string | null;
  shouldTradeNow: boolean | null;

  // Auto-generated summaries (deterministic, no AI call)
  whyItMattered: string;
  whyActionable: string;

  // Human input
  decision: JournalDecision | null;
  decisionReason: string;
  executionQuality: ExecutionQuality | null;
  emotionState: EmotionState | null;
  traderNotes: string;
  outcome: JournalDraftOutcome;
  outcomeNotes: string;

  // Linkage
  researchSnapshotId: string | null;
  savedToJournal: boolean;
}

export interface JournalEntry {
  id: number;
  date: string;
  symbol: string;
  setup: string;
  regime: string;
  dir: string;
  entry: number | null;
  stop: number | null;
  target: number | null;
  exit: number | null;
  result: number | null;
  rMultiple: number | null;
  notes: string;
  tags: string[];
  outcome: JournalOutcome;
}

// ─── Trading Performance Audit ───────────────────────────────────────────────

export type AuditStatus  = 'open' | 'closed' | 'skipped' | 'invalidated';
export type AuditOutcome = 'win' | 'loss' | 'breakeven' | 'open' | 'skipped' | 'invalidated';
export type TrendAlignmentBucket = 'aligned_with_trend' | 'counter_trend' | 'neutral_trend_context';
export type ConfidenceBucket = '0-39' | '40-54' | '55-69' | '70-84' | '85+';

export interface AuditRecord {
  id:                  string;   // uuid-like: symbol_ts
  createdAt:           number;
  symbol:              string;
  interval:            string;
  setupType:           string;
  direction:           'long' | 'short';
  regime:              string;
  phase:               string;   // RANGE | EXPANSION | POST_EXPANSION
  trendDirection:      string;
  trendState:          string;
  trendStrength:       number;
  smcBias:             string;
  displacementDirection: string;
  scenarioType:        string;
  confidence:          number;
  readiness:           string;
  entryPrice:          number | null;
  stopPrice:           number | null;
  target1Price:        number | null;
  target2Price:        number | null;
  status:              AuditStatus;
  outcome:             AuditOutcome;
  exitPrice:           number | null;
  exitReason:          string;
  realizedR:           number | null;
  mfe:                 number | null;   // max favorable excursion (in R)
  mae:                 number | null;   // max adverse excursion (in R)
  trendAlignment:      TrendAlignmentBucket;
  confidenceBucket:    ConfidenceBucket;
  notes:               string;
  tags:                string[];

  // ── setup2 rich semantics (optional — populated when setup2 is available) ───
  setup2_classification?: string | null;  // e.g. 'CONTINUATION', 'NO_VALID_SETUP'
  setup2_state?:          string | null;  // 'READY' | 'DEVELOPING' | 'QUALIFIED' etc.
  setup2_score?:          number | null;  // 0–100 weighted setup quality
  setup2_readiness?:      number | null;  // 0–100 independent readiness
  setup2_direction?:      string | null;  // 'LONG' | 'SHORT' | 'NEUTRAL'
  is_actionable?:         boolean | null; // explicit actionability gate result
  is_no_valid_setup?:     boolean | null; // true = NO_VALID_SETUP was emitted
  setup2_penalties?:      Array<{ reason: string; points: number }>;
  setup2_disqualifiers?:  string[];       // fatal disqualifier rule IDs

  // ── thesis2 rich semantics (optional) ───────────────────────────────────────
  thesis_state?:          string | null;  // e.g. 'BULLISH' | 'BEARISH' | 'WAIT'
  action_posture?:        string | null;  // e.g. 'TAKE_TRADE' | 'NO_TRADE'
  thesis_conviction?:     number | null;  // 0–100
  thesis_actionability?:  number | null;  // 0–100

  // ── ranking semantics (optional) ────────────────────────────────────────────
  rank_score?:            number | null;  // composite opportunity score
  rank_bucket?:           string | null;  // 'top1' | 'top3' | 'top5' | 'rest'
}

export interface AuditMetrics {
  total:               number;
  executed:            number;
  closed:              number;
  winRate:             number | null;
  avgR:                number | null;
  medianR:             number | null;
  expectancy:          number | null;
  avgMFE:              number | null;
  avgMAE:              number | null;
  bySetupType:         Record<string, { count: number; winRate: number | null; avgR: number | null }>;
  byDirection:         Record<string, { count: number; winRate: number | null; avgR: number | null }>;
  byPhase:             Record<string, { count: number; winRate: number | null; avgR: number | null }>;
  byTrendAlignment:    Record<TrendAlignmentBucket, { count: number; winRate: number | null; avgR: number | null }>;
  byConfidenceBucket:  Record<ConfidenceBucket, { count: number; winRate: number | null; avgR: number | null; calibration: string }>;
  byReadiness:         Record<string, { count: number; winRate: number | null; avgR: number | null }>;
  continuationVsReversal: {
    continuation: {
      total:      number;
      winRate:    number | null;
      avgR:       number | null;
      medianR:    number | null;
      expectancy: number | null;
    };
    reversal: {
      total:      number;
      winRate:    number | null;
      avgR:       number | null;
      medianR:    number | null;
      expectancy: number | null;
    };
  };
  rvolContext:         { low: { count: number; avgR: number | null }; normal: { count: number; avgR: number | null } };
  bestSetupFamilies:   Array<{ setupType: string; expectancy: number; count: number }>;
  worstSetupFamilies:  Array<{ setupType: string; expectancy: number; count: number }>;
  confidenceCalibrated: boolean;
}

export interface AppSettings {
  tolerance: number;
  rvolWindow: number;
  minSwingBars: number;

  defaultInterval: 'tick' | '1sec' | '5sec' | '15sec' | '30sec' | '1min' | '5min' | '15min';
  smcSensitivity: 'low' | 'medium' | 'high';
  // Auto-refresh / real-time
  autoRefresh: boolean;           // poll on a timer when market is open
  refreshIntervalSec: number;     // polling cadence in seconds (15 | 30 | 60 | 300)
  autoFetchOnSymbolSwitch: boolean; // fetch immediately when switching watchlist symbol
  anthropicApiKey: string;          // only needed for local npm start; not required in claude.ai
  // AI Orchestration
  autoAIMode: 'off' | 'major_only' | 'all';  // event-driven AI refresh mode
  aiCooldownSec: number;                       // min seconds between AI calls per symbol
  aiActiveSymbolOnly: boolean;                 // only run AI on active symbol vs all watchlist
  // Risk Model (position sizing)
  accountSize:          number | null;         // null = normalized mode (no dollar amounts)
  riskPerTradePct:      number;                // % of account to risk per trade (default 1%)
  maxConcurrentTrades:  number;                // max simultaneous setups tracked (default 5)
  maxSameThemePct:      number;                // max % of risk in one theme (default 30%)
  maxSameDirectionPct:  number;                // max % of risk in one direction (default 60%)
}

export const DEFAULT_SETTINGS: AppSettings = {
  tolerance: 0.3,
  rvolWindow: 20,
  minSwingBars: 3,

  defaultInterval: '1min',        // default: 1-minute candles — required for reliable SMC structure
  smcSensitivity: 'low',          // low = stricter thresholds = fewer false signals
  autoRefresh: true,
  refreshIntervalSec: 60,
  autoFetchOnSymbolSwitch: true,
  anthropicApiKey: '',  // only needed for local npm start; not required in claude.ai
  autoAIMode: 'major_only',
  aiCooldownSec: 300,             // 5 minutes — gives thesis time to develop before replacing
  aiActiveSymbolOnly: true,
  accountSize:          null,     // not configured — normalized sizing mode
  riskPerTradePct:      1.0,
  maxConcurrentTrades:  5,
  maxSameThemePct:      30,
  maxSameDirectionPct:  60,
};

// ─── SMC Types ────────────────────────────────────────────────────────────────

export type SMCBias = 'bullish' | 'bearish' | 'neutral';
export type SMCStrength = 'major' | 'minor';

// Market phase — lightweight phase model for displacement/expansion awareness
export type MarketPhase = 'RANGE' | 'EXPANSION' | 'POST_EXPANSION';

// Scenario classifier — sweep + displacement sequencing
export type ScenarioType = 'reversal' | 'continuation' | 'transition' | 'none';

// Liquidity side
export type LiquiditySide = 'buy_side' | 'sell_side';

// ── Trend engine types ────────────────────────────────────────────────────────
export type TrendDirection = 'bullish' | 'bearish' | 'neutral';
export type TrendState     = 'trend' | 'pullback' | 'compression' | 'transition';

export interface TrendResult {
  ema9:           number;
  ema21:          number;
  slope9:         number;
  slope21:        number;
  trendDirection: TrendDirection;
  trendState:     TrendState;
  trendStrength:  number;   // 0..1
  alignmentScore: number;   // 0..1
}
export type FVGStatus = 'open' | 'mitigated' | 'partially_mitigated';
export type OBType = 'bullish' | 'bearish';

export interface StructureEvent {
  type: 'BOS' | 'CHOCH';
  dir: 'bullish' | 'bearish';
  price: number;
  candleIdx: number;
  ts: number;
  strength: SMCStrength;
  description: string;
}

export interface FairValueGap {
  id: string;
  dir: 'bullish' | 'bearish';
  top: number;
  bottom: number;
  midpoint: number;
  candleIdx: number;
  ts: number;
  status: FVGStatus;
  filled: number;
  size: number;
  significance: SMCStrength;
}

export interface OrderBlock {
  id: string;
  type: OBType;
  top: number;
  bottom: number;
  midpoint: number;
  candleIdx: number;
  ts: number;
  mitigated: boolean;
  strength: SMCStrength;
  associatedBOS: boolean;
  description: string;
}

export interface LiquiditySweep {
  type: 'sweep_high' | 'sweep_low';
  level: number;
  candleIdx: number;
  ts: number;
  reclaimed: boolean;
  description: string;
}

export interface Displacement {
  dir: 'bullish' | 'bearish';
  candleIdx: number;
  ts: number;
  size: number;
  volumeMultiple: number;
  description: string;
}

export interface PremiumDiscount {
  rangeHigh: number;
  rangeLow: number;
  equilibrium: number;
  currentPrice: number;
  zone: 'premium' | 'discount' | 'equilibrium';
  positionPct: number;
  description: string;
}

export interface SMCResult {
  bias: SMCBias;
  biasStrength: SMCStrength;
  structureEvents: StructureEvent[];
  lastBOS: StructureEvent | null;
  lastCHOCH: StructureEvent | null;
  fvgAbove: FairValueGap | null;
  fvgBelow: FairValueGap | null;
  allFVGs: FairValueGap[];
  obAbove: OrderBlock | null;
  obBelow: OrderBlock | null;
  allOBs: OrderBlock[];
  recentSweep: LiquiditySweep | null;
  sweepAndReverse: boolean;
  recentDisplacement: Displacement | null;
  displacementAligned: boolean;
  premiumDiscount: PremiumDiscount | null;
  confidenceScore: number;
  summary: string;
  keySignal: string;
  // ── Phase / event fields ──────────────────────────────────────────────────
  displacementDirection:   'bullish' | 'bearish' | 'none';
  displacementStrength:    number;   // 0..1
  displacementFreshness:   number;   // 0..1 (decays with bars since event)
  displacementBreaksStructure: boolean;
  displacementCreatesImbalance: boolean;
  institutionalEventVolume: boolean;
  eventVolumeScore:        number;   // 0..1
  lastLiquidityTaken:      LiquiditySide | null;
  expectedNextLiquidity:   LiquiditySide | null;
  liquiditySequenceConfidence: number;  // 0..1
  scenarioType:            ScenarioType;
  bestFVG:                 FairValueGap | null;  // highest-ranked FVG by quality
}

export interface SMCOverlaySettings {
  showBOSCHOCH: boolean;
  showFVGs: boolean;
  showOrderBlocks: boolean;
  showSweeps: boolean;
  showPremiumDiscount: boolean;
}

export const DEFAULT_SMC_OVERLAYS: SMCOverlaySettings = {
  showBOSCHOCH: true,
  showFVGs: true,
  showOrderBlocks: true,
  showSweeps: true,
  showPremiumDiscount: false,
};

// ─── TradePlan (v5 additive — decision support only) ─────────────────────────
// Descriptive planning tool. Non-executing. No advisory language.

export type TradePlanStatus =
  | 'planned'      // created, not yet taken
  | 'taken'        // trader manually logged as entered
  | 'skipped'      // consciously passed
  | 'expired'      // session ended without action
  | 'invalidated'; // key level / thesis broke

export interface TradePlan {
  id:              string;
  createdAt:       number;
  symbol:          string;
  interval:        string;

  // Setup context (from engine)
  setupType:       string;
  direction:       'long' | 'short';

  // AI context
  bias:            string;          // 'bullish' | 'bearish' | 'neutral' | 'mixed'
  conviction:      number;          // 0–100
  actionability:   number;          // 0–100

  // Regime context
  regime:          string;
  phase:           string;          // RANGE | EXPANSION | POST_EXPANSION
  trendDirection:  string;
  trendState:      string;
  trendStrength:   number;
  smcBias:         string;
  scenarioType:    string;

  // Execution considerations (descriptive — NOT instructions)
  entryPlanned:         string;     // description of intended entry zone
  confirmationRule:     string;     // what must print before acting
  stopPlanned:          number | null;   // intended stop level (reference only)
  target1Planned:       number | null;
  target2Planned:       number | null;
  managementPlan:       string;     // how position will be managed if taken
  abortRule:            string;     // conditions that would cancel the plan

  // Risk context (analytical — no $ amounts unless account configured)
  riskTierSuggested:    'light' | 'standard' | 'reduced' | 'avoid';

  // Outcome tracking
  status:               TradePlanStatus;
  linkedAuditId:        string | null;  // link to AuditRecord if taken

  // Metadata
  notes:                string;
  tags:                 string[];
}

// ─── WaitRecord (v5 additive) ────────────────────────────────────────────────
// Tracks conscious no-trade / wait decisions for behavioral analytics.

export type WaitClassification =
  | 'correct_avoidance'    // no-trade was right (price moved adversely)
  | 'missed_opportunity'   // setup would have worked
  | 'neutral'              // outcome ambiguous / not tracked
  | 'pending';             // not yet classified

export interface WaitRecord {
  id:              string;
  symbol:          string;
  timestamp:       number;
  reason:          string;          // why trade was avoided
  regimeAtTime:    string;
  phaseAtTime:     string;
  setupTypeAvoided: string;
  directionAvoided: 'long' | 'short' | 'both';
  classification:  WaitClassification;
  priceAtTime:     number | null;
  notes:           string;
}

// ─── Phase 7.1A — Observability Journal ──────────────────────────────────────
// Additive only. Never feeds back into ranking, scoring, or setup logic.

export type Journal71AActionType =
  | 'setup_viewed'
  | 'trade_taken'
  | 'trade_skipped'
  | 'note_added'
  | 'outcome_reviewed';

export interface Journal71AEntry {
  id:          string;          // uuid-style: symbol_ts
  ts:          number;          // Unix ms
  symbol:      string;
  actionType:  Journal71AActionType;

  // Setup context at time of entry (read-only snapshot)
  setupType:   string | null;
  direction:   'long' | 'short' | 'neutral' | null;
  score:       number | null;   // totalScore at time of entry
  confidence:  number | null;   // aiConviction at time of entry
  regime:      string | null;
  gammaStatus: string | null;   // 'applied' | 'disabled' | 'unavailable' | 'not_applied'

  // Phase 7.1B: ordered scanner snapshot at time of log (lightweight, no engine refs)
  rankedSnapshot?: Array<{
    symbol:    string;
    type:      string;
    score:     number;
    direction: string;
    rank:      number;   // 1-based position in allScores at log time
  }>;

  // Human input
  note:        string;
  outcome:     'win' | 'loss' | 'breakeven' | null;  // only for outcome_reviewed
}

// ─── Extended AuditMetrics additions (v5 additive) ───────────────────────────
// Added to AuditMetrics as optional so existing consumers are unaffected.

export interface DrawdownInfo {
  maxDrawdown:        number | null;   // worst peak-to-trough in R
  currentDrawdown:    number | null;   // current drawdown from peak equity
  peakEquityR:        number | null;
  currentEquityR:     number | null;
}

export interface StreakInfo {
  currentStreak:    number;   // positive = win streak, negative = loss streak
  maxWinStreak:     number;
  maxLossStreak:    number;
  bestWinningSetup: string;   // setup type with most cumulative wins (not consecutive streak)
}

export interface ExecutionQualityStats {
  avgSlippage:        number | null;   // average entry vs planned in R (manual input — not yet tracked)
  avgExitEfficiency:  number | null;   // % of max possible gain captured (not yet tracked)
  avgMFECaptured:     number | null;   // % of MFE captured at exit (when MFE logged)
  avgMAE:             number | null;   // average max adverse excursion in R (when MAE logged)
}

export interface WaitAccuracyStats {
  total:                number;
  correctAvoidances:    number;
  missedOpportunities:  number;
  pending:              number;
  avoidanceRate:        number | null;  // correctAvoidances / (correctAvoidances + missedOpportunities)
}

export interface BehavioralStats {
  avgHoldBars:        number | null;   // estimated bars held (from entry/exit times)
  overtradeScore:     number | null;   // 0–1: how often consecutive same-day setups appear
  bestTimeOfDay:      string | null;   // session bucket with best expectancy
  worstTimeOfDay:     string | null;
}

export interface EquityCurvePoint {
  timestamp:          number;
  cumulativeR:        number;
  tradeIndex:         number;
  setupType:          string;
  outcome:            string;
}

// ─── Quant Intelligence Layer (v5.3 additive) ─────────────────────────────────
// Types for the three quant modules. All additive — no existing types affected.

export type { ConfidenceCalibrationBucketStats, ConfidenceCalibrationSummary }
  from '../../services/quant/confidenceCalibration';

export type { ExpectancyMatrixCell, ExpectancyMatrixSummary }
  from '../../services/quant/expectancyMatrix';

export type {
  DecisionQualityBreakdown,
  DecisionQualityResult,
  DecisionQualitySummary,
} from '../../services/quant/decisionQuality';

// ─── Adaptive Edge System (v5.5 additive) ─────────────────────────────────────
// Types for the three adaptive modules. All additive — no existing types affected.

export type { AdaptiveConfidenceBucket, AdaptiveConfidenceSummary }
  from '../../services/adaptive/adaptiveConfidence';

export type { AdaptiveSetupContextWeight, AdaptiveSetupWeightsSummary }
  from '../../services/adaptive/adaptiveSetupWeights';

export type { ExposureBreakdown, ExposureWarning, ExposureIntelligenceSummary }
  from '../../services/adaptive/exposureIntelligence';

export type { AdaptiveScoreAdjustments, AdaptiveScoreResult, AdaptiveScoreSummary }
  from '../../services/adaptive/adaptiveScoring';
